/**
 * SettingsPage — 프로필, 알림, 구독/결제, 계정 관리
 */

import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { CreditCard, Sparkles } from 'lucide-react'
import { useAuth } from '../../lib/auth.js'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Input,
  Pagination,
  Spinner,
  StatCard,
  Table,
  Toggle,
} from '../../components/ui/index.js'
import type { TableColumn } from '../../components/ui/index.js'
import styles from './SettingsPage.module.css'

const PROVIDER_LABELS: Record<string, string> = {
  kakao: '카카오',
  naver: '네이버',
}

const PLAN_LABELS: Record<string, string> = {
  FREE: '무료',
  PREMIUM: '프리미엄',
}

const STATUS_LABELS: Record<string, { label: string; color: 'gray' | 'green' | 'red' | 'yellow' }> = {
  COMPLETED: { label: '완료', color: 'green' },
  PENDING: { label: '대기중', color: 'yellow' },
  FAILED: { label: '실패', color: 'red' },
  REFUNDED: { label: '환불', color: 'gray' },
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

function formatAmount(amount: number) {
  return `${amount.toLocaleString('ko-KR')}원`
}

const paymentColumns: TableColumn<PaymentItem>[] = [
  {
    key: 'date',
    label: '날짜',
    width: '100px',
    render: (row) => formatDate(row.date),
  },
  {
    key: 'description',
    label: '내용',
  },
  {
    key: 'amount',
    label: '금액',
    width: '100px',
    align: 'right',
    render: (row) => formatAmount(row.amount),
  },
  {
    key: 'status',
    label: '상태',
    width: '80px',
    align: 'center',
    render: (row) => {
      const cfg = STATUS_LABELS[row.status]
      return cfg
        ? <Badge color={cfg.color}>{cfg.label}</Badge>
        : <Badge>{row.status}</Badge>
    },
  },
]

interface LinkedAccount {
  provider: string
  providerAccountId: string
}

interface UserProfile {
  id: string
  email: string
  name: string | null
  image: string | null
  role: string
  premiumPlan: string
  aiCredits: number
  emailNotifications: boolean
  createdAt: string
  isPremium: boolean
}

interface PaymentItem {
  id: string
  date: string
  amount: number
  description: string
  status: string
  method: string
  channel: string | null
  orderId: string | null
}

interface PaymentsResponse {
  payments: PaymentItem[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export function SettingsPage() {
  const { user, refreshUser, logout } = useAuth()
  const navigate = useNavigate()

  // 프로필 데이터 (emailNotifications 포함)
  const { data: profile } = useApiFetch<UserProfile>('/api/user/profile')

  // 프로필 이름 편집
  const [name, setName] = useState(user?.name ?? '')
  const [nameChanged, setNameChanged] = useState(false)

  // 연결된 계정
  const { data: linkedAccounts, isLoading: accountsLoading } =
    useApiFetch<LinkedAccount[]>('/api/user/accounts')

  // 설정 mutation
  const { trigger: updateSettings, isLoading: saving } = useApiMutation<
    { emailNotifications: boolean; name: string | null },
    { emailNotifications?: boolean; name?: string }
  >('/api/user/settings', 'PATCH')

  // 계정 삭제
  const { trigger: deleteAccount, isLoading: deleting } = useApiMutation(
    '/api/user/delete',
    'DELETE',
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // 결제 내역
  const [paymentPage, setPaymentPage] = useState(1)
  const { data: paymentsData, isLoading: paymentsLoading } =
    useApiFetch<PaymentsResponse>(`/api/payments/history?page=${paymentPage}&pageSize=5`)

  // 알림 토글
  const [emailNotifications, setEmailNotifications] = useState(true)

  useEffect(() => {
    if (profile) {
      setEmailNotifications(profile.emailNotifications)
    }
  }, [profile])

  const handleNameSave = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const result = await updateSettings({ name: trimmed })
    if (result) {
      setNameChanged(false)
      await refreshUser()
    }
  }, [name, updateSettings, refreshUser])

  const handleNotificationToggle = useCallback(
    async (checked: boolean) => {
      setEmailNotifications(checked)
      await updateSettings({ emailNotifications: checked })
    },
    [updateSettings],
  )

  const handleDeleteAccount = useCallback(async () => {
    const result = await deleteAccount()
    if (result !== null) {
      await logout()
      navigate('/')
    }
  }, [deleteAccount, logout, navigate])

  if (!user) return <Spinner />

  return (
    <div className={styles.root}>
      <h1 className={styles.pageTitle}>설정</h1>

      {/* 좌측: 프로필, 연결된 계정, 위험 영역 */}
      <div className={styles.leftCol}>
        {/* 프로필 */}
        <Card title="프로필">
          <div className={styles.profileSection}>
            <Avatar src={user.image ?? undefined} name={user.name ?? user.email} size="xl" />
            <div className={styles.profileInfo}>
              <div className={styles.nameRow}>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setNameChanged(e.target.value.trim() !== (user.name ?? ''))
                  }}
                  placeholder="이름을 입력하세요"
                />
                <Button
                  size="sm"
                  onClick={handleNameSave}
                  disabled={!nameChanged || !name.trim() || saving}
                >
                  저장
                </Button>
              </div>
              <span className={styles.emailText}>{user.email}</span>
            </div>
          </div>
        </Card>

        {/* 연결된 계정 */}
        <Card title="연결된 계정">
          {accountsLoading ? (
            <Spinner />
          ) : linkedAccounts && linkedAccounts.length > 0 ? (
            <div>
              {linkedAccounts.map((account) => (
                <div key={account.provider} className={styles.accountItem}>
                  <div className={`${styles.accountIcon} ${styles[account.provider]}`}>
                    {account.provider === 'kakao' ? 'K' : 'N'}
                  </div>
                  <span className={styles.accountName}>
                    {PROVIDER_LABELS[account.provider] ?? account.provider}
                  </span>
                  <Badge color="green">연결됨</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.emptyText}>연결된 계정이 없습니다</p>
          )}
        </Card>

        {/* 계정 삭제 */}
        <Card title="위험 영역" className={styles.dangerCard}>
          <p className={styles.dangerText}>
            계정을 삭제하면 모든 청첩장, AI 사진, 설정이 영구적으로 삭제됩니다.
            이 작업은 되돌릴 수 없습니다.
          </p>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
          >
            계정 삭제
          </Button>
        </Card>
      </div>

      {/* 우측: 알림, 구독/크레딧, 결제 내역 */}
      <div className={styles.rightCol}>
        {/* 알림 */}
        <Card title="알림">
          <div className={styles.notificationRow}>
            <div className={styles.notificationLabel}>
              <span className={styles.notificationTitle}>이메일 알림</span>
              <span className={styles.notificationDesc}>
                RSVP, 방명록 등 새 알림을 이메일로 받습니다
              </span>
            </div>
            <Toggle
              checked={emailNotifications}
              onChange={handleNotificationToggle}
            />
          </div>
        </Card>

        {/* 구독 / 크레딧 */}
        <Card title="구독 및 크레딧">
          <div className={styles.statsRow}>
            <StatCard
              label="현재 플랜"
              value={PLAN_LABELS[user.premiumPlan] ?? user.premiumPlan}
              icon={<CreditCard size={16} />}
              className={styles.compactStat}
            />
            <StatCard
              label="AI 크레딧"
              value={`${user.aiCredits}개`}
              icon={<Sparkles size={16} />}
              className={styles.compactStat}
            />
          </div>
        </Card>

        {/* 결제 내역 */}
        <Card title="결제 내역">
          <Table<PaymentItem>
            columns={paymentColumns}
            data={paymentsData?.payments ?? []}
            rowKey={(row) => row.id}
            size="sm"
            isLoading={paymentsLoading}
            emptyMessage="결제 내역이 없습니다"
          />
          {paymentsData && paymentsData.pagination.totalPages > 1 && (
            <div className={styles.paginationWrap}>
              <Pagination
                current={paymentsData.pagination.page}
                total={paymentsData.pagination.totalPages}
                onChange={setPaymentPage}
                size="sm"
              />
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="계정 삭제"
        message="정말 계정을 삭제하시겠습니까? 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다."
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteAccount}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}
